import { DEMOGRAPHIC_IDS, MajorityMinorityData } from "../../types/report";
import { GetDemographicGroupsResponse } from "../../types/workplaceCulture";
import { findDemographicById } from "../../utils/demographics.utils";

/**
 * Extracts majority and minority group data from the Marginalised People demographic group.
 */
export const getMajorityMinorityData = (
  demographicsData?: GetDemographicGroupsResponse
): MajorityMinorityData | undefined => {
  if (!demographicsData?.demographic_groups) return undefined;

  const majorityDemographic = findDemographicById(
    demographicsData,
    DEMOGRAPHIC_IDS.MAJORITY_GROUPS
  );
  const minorityDemographic = findDemographicById(
    demographicsData,
    DEMOGRAPHIC_IDS.MARGINALISED_GROUPS
  );

  if (!majorityDemographic || !minorityDemographic) {
    return undefined;
  }

  return {
    majorityScore: majorityDemographic.score ?? undefined,
    minorityScore: minorityDemographic.score ?? undefined,
    majorityCount: majorityDemographic.engagement,
    minorityCount: minorityDemographic.engagement,
    majorityUnderThreshold: majorityDemographic.is_under_threshold,
    minorityUnderThreshold: minorityDemographic.is_under_threshold,
  };
};

/**
 * Gets the translation key for the majority/minority description based on scores.
 * Returns a key suffix that should be appended to "majorityMinorityGroups.description."
 */
export const getMajorityMinorityDescriptionKey = (
  overallScore: number,
  majorityScore?: number,
  minorityScore?: number
): { openingKey: string; comparisonKey: string } => {
  // Step 1: Determine opening line based on overall score
  let openingKey: string;
  if (overallScore <= 2.5) {
    openingKey = "openingLow";
  } else if (overallScore <= 4.0) {
    openingKey = "openingMedium";
  } else {
    openingKey = "openingHigh";
  }

  // Step 2: Determine comparison text based on score difference
  let comparisonKey: string;

  // If either score is missing, use a default
  if (
    majorityScore === undefined ||
    minorityScore === undefined ||
    majorityScore === null ||
    minorityScore === null
  ) {
    comparisonKey = "comparisonNoData";
  } else {
    const difference = majorityScore - minorityScore;

    if (difference > 0.2) {
      // A: Minority groups score lower by more than 0.2
      comparisonKey = "comparisonMinorityLower";
    } else if (Math.abs(difference) < 0.01) {
      // B: Scores are tied
      comparisonKey = "comparisonTied";
    } else if (difference >= 0.01 && difference <= 0.2) {
      // C: Small gap in favour of majority groups
      comparisonKey = "comparisonSmallGapMajority";
    } else if (minorityScore - majorityScore > 0.2) {
      // D: Minority groups score significantly higher
      comparisonKey = "comparisonMinorityHigher";
    } else {
      // E: Small gap in favour of minority groups (0.01 ≤ minority - majority ≤ 0.2)
      comparisonKey = "comparisonSmallGapMinority";
    }
  }

  return { openingKey, comparisonKey };
};
